/**
 * Permission Type Definitions - V3 Permission-Based Authorization
 *
 * This file defines the Permission type and related constants for the V3
 * authorization system. Permissions follow a resource:action pattern.
 *
 * @see specs/auth-permission-refactor-v3/spec.md
 * @see packages/services/src/role-service.ts
 */

// ============================================================================
// Permission Type
// ============================================================================

/**
 * V3 Permission type - All valid permission strings
 *
 * Permissions follow the pattern: resource:action
 * - Wildcard "*" grants all permissions (super-admin only)
 * - read/write for data access
 * - manage for administrative actions
 * - assign for task assignment capability
 */
export type Permission =
  | "*"
  | "users:read"
  | "users:write"
  | "persons:read"
  | "persons:write"
  | "tasks:read"
  | "tasks:write"
  | "tasks:create"
  | "tasks:assign"
  | "billing:read"
  | "billing:write"
  | "billing:export"
  | "organization-hours:read"
  | "settings:read"
  | "settings:write"
  | "teams:manage"
  | "roles:manage"
  | "activityTypes:manage"
  | "campaigns:send"
  | "campaigns:settings"
  | "automation:manage"
  | "users:create"
  | "users:update"
  | "users:deactivate"
  | "users:delete"
  | "users:import"
  | "users:assign-role"
  | "users:assign-team"
  | "users:adjust-hours"
  | "users:update-identity"
  | "users:activate"
  | "users:assign-managed-teams"
  | "roles:read"
  | "roles:create"
  | "roles:update"
  | "roles:delete"
  | "roles:assign"
  | "persons:create"
  | "persons:update"
  | "persons:delete"
  | "persons:link"
  | "persons:assign-team"
  | "persons:activate"
  | "persons:deactivate"
  | "persons:adjust-hours"
  | "persons:manage-billing"
  | "teams:read"
  | "teams:create"
  | "teams:update"
  | "teams:delete"
  | "teams:assign-members"
  | "activities:read"
  | "activities:create"
  | "activities:update"
  | "activities:delete"
  | "activities:register-self"
  | "activities:assign-users"
  | "activities:complete"
  | "hours:read"
  | "hours:create"
  | "hours:update"
  | "hours:delete"
  | "hours:approve"
  | "reports:read-summary"
  | "reports:export"
  | "settings:update"
  | "billing:manage"

// ============================================================================
// Permission Constants
// ============================================================================

/**
 * All available permissions for UI selectors and validation.
 * Does NOT include wildcard "*" as it should not be selectable in UI.
 */
export const ALL_PERMISSIONS: Permission[] = [
  "users:read",
  "users:write",
  "persons:read",
  "persons:write",
  "tasks:read",
  "tasks:write",
  "tasks:create",
  "tasks:assign",
  "billing:read",
  "billing:write",
  "billing:export",
  "organization-hours:read",
  "settings:read",
  "settings:write",
  "teams:manage",
  "roles:manage",
  "activityTypes:manage",
  "campaigns:send",
  "campaigns:settings",
  "automation:manage",
]

/** Target permissions not yet exposed by the legacy production role editor. */
export const GRANULAR_PERMISSIONS: Permission[] = [
  "users:read",
  "users:create",
  "users:update",
  "users:deactivate",
  "users:delete",
  "users:assign-role",
  "users:assign-team",
  "users:adjust-hours",
  "users:update-identity",
  "users:activate",
  "users:assign-managed-teams",
  "users:import",
  "roles:read",
  "roles:create",
  "roles:update",
  "roles:delete",
  "persons:read",
  "persons:create",
  "persons:update",
  "persons:delete",
  "persons:link",
  "persons:assign-team",
  "persons:activate",
  "persons:deactivate",
  "persons:adjust-hours",
  "persons:manage-billing",
  "teams:read",
  "teams:create",
  "teams:update",
  "teams:delete",
  "activities:read",
  "activities:create",
  "activities:update",
  "activities:delete",
  "activities:register-self",
  "activities:assign-users",
  "activities:complete",
  "hours:read",
  "hours:create",
  "hours:update",
  "hours:delete",
  "hours:approve",
  "reports:read-summary",
  "reports:export",
  "settings:read",
  "settings:update",
  "billing:manage",
  "billing:read",
  "billing:write",
  "billing:export",
  "organization-hours:read",
  "activityTypes:manage",
  "campaigns:send",
  "campaigns:settings",
  "automation:manage",
]

export interface PermissionGroupDefinition {
  id: string
  label: string
  description: string
  permissions: readonly Permission[]
}

/** Canonical role-editor chart. Migration aliases are deliberately excluded. */
export const GRANULAR_PERMISSION_GROUPS: readonly PermissionGroupDefinition[] =
  [
    {
      id: "users",
      label: "Brukere",
      description: "Brukerkontoer, roller og gruppetilhørighet",
      permissions: [
        "users:read",
        "users:create",
        "users:update",
        "users:update-identity",
        "users:activate",
        "users:deactivate",
        "users:delete",
        "users:import",
        "users:assign-role",
        "users:assign-team",
        "users:assign-managed-teams",
        "users:adjust-hours",
      ],
    },
    {
      id: "persons",
      label: "Medlemmer og koblinger",
      description: "Medlemsprofiler, foresatte og gruppetilhørighet",
      permissions: [
        "persons:read",
        "persons:create",
        "persons:update",
        "persons:activate",
        "persons:deactivate",
        "persons:delete",
        "persons:link",
        "persons:assign-team",
        "persons:adjust-hours",
        "persons:manage-billing",
      ],
    },
    {
      id: "teams",
      label: "Grupper",
      description: "Gruppeinformasjon og struktur",
      permissions: [
        "teams:read",
        "teams:create",
        "teams:update",
        "teams:delete",
      ],
    },
    {
      id: "activities",
      label: "Aktiviteter",
      description: "Aktiviteter, påmeldinger og fullføring",
      permissions: [
        "activities:read",
        "activities:create",
        "activities:update",
        "activities:delete",
        "activities:register-self",
        "activities:assign-users",
        "activities:complete",
      ],
    },
    {
      id: "hours",
      label: "Timer",
      description: "Timeregistrering og godkjenning",
      permissions: [
        "hours:read",
        "hours:create",
        "hours:update",
        "hours:delete",
        "hours:approve",
      ],
    },
    {
      id: "reports",
      label: "Rapporter",
      description: "Oversikter og eksport",
      permissions: ["reports:read-summary", "reports:export"],
    },
    {
      id: "roles",
      label: "Roller",
      description: "Roller og tillatelser for hele organisasjonen",
      permissions: [
        "roles:read",
        "roles:create",
        "roles:update",
        "roles:delete",
      ],
    },
    {
      id: "organization",
      label: "Organisasjon og fakturering",
      description: "Felles innstillinger og fakturering",
      permissions: ["settings:read", "settings:update", "billing:manage", "billing:read", "billing:write", "billing:export", "organization-hours:read"],
    },
    {
      id: "operations",
      label: "Aktivitetstyper, meldinger og automatisering",
      description: "Organisasjonens aktivitetstyper og meldingsinnstillinger",
      permissions: ["activityTypes:manage", "campaigns:send", "campaigns:settings", "automation:manage"],
    },
  ]

/** All recognized persisted permissions during the migration period. */
export const VALID_PERMISSIONS: Permission[] = [
  ...new Set([...ALL_PERMISSIONS, ...GRANULAR_PERMISSIONS]),
]

/** Permissions without a meaningful assigned-team target. */
export const TENANT_ONLY_PERMISSIONS: ReadonlySet<Permission> = new Set([
  "roles:read",
  "roles:create",
  "roles:update",
  "roles:delete",
  "roles:assign",
  "roles:manage",
  "users:assign-role",
  "users:update-identity",
  "users:assign-managed-teams",
  "users:import",
  "settings:read",
  "settings:update",
  "settings:write",
  "billing:manage",
  "billing:read",
  "billing:write",
  "billing:export",
  "persons:manage-billing",
  "organization-hours:read",
  "activityTypes:manage",
  "campaigns:send",
  "campaigns:settings",
  "automation:manage",
  "teams:create",
])

export const isTenantOnlyPermission = (permission: Permission): boolean =>
  TENANT_ONLY_PERMISSIONS.has(permission)

/**
 * Capabilities automatically required by action permissions.
 *
 * Keep this map centralized so authorization, validation, and role-management
 * UI use the same dependency graph.
 */
export const PERMISSION_DEPENDENCIES: Readonly<
  Partial<Record<Permission, readonly Permission[]>>
> = {
  "users:write": ["users:read"],
  "persons:write": ["persons:read"],
  "tasks:write": ["tasks:read"],
  "tasks:create": ["tasks:read"],
  "tasks:assign": ["tasks:read"],
  "billing:write": ["billing:read"],
  "settings:write": ["settings:read"],
  "users:create": ["users:read"],
  "users:update": ["users:read"],
  "users:deactivate": ["users:read"],
  "users:delete": ["users:read"],
  "users:import": ["users:read"],
  "users:assign-role": ["users:read", "roles:read"],
  "users:assign-team": ["users:read", "teams:read"],
  "users:adjust-hours": ["users:read", "hours:read"],
  "users:update-identity": ["users:read"],
  "users:activate": ["users:read"],
  "users:assign-managed-teams": ["users:read", "teams:read"],
  "roles:create": ["roles:read"],
  "roles:update": ["roles:read"],
  "roles:delete": ["roles:read"],
  "roles:assign": ["users:read", "roles:read"],
  "persons:create": ["persons:read"],
  "persons:update": ["persons:read"],
  "persons:delete": ["persons:read"],
  "persons:link": ["persons:read", "users:read"],
  "persons:assign-team": ["persons:read", "teams:read"],
  "persons:activate": ["persons:read"],
  "persons:deactivate": ["persons:read"],
  "persons:adjust-hours": ["persons:read", "hours:read"],
  "persons:manage-billing": ["persons:read"],
  "teams:create": ["teams:read"],
  "teams:update": ["teams:read"],
  "teams:delete": ["teams:read"],
  "teams:assign-members": ["teams:read"],
  "activities:create": ["activities:read"],
  "activities:update": ["activities:read"],
  "activities:delete": ["activities:read"],
  "activities:register-self": ["activities:read"],
  "activities:assign-users": ["activities:read", "users:read"],
  "activities:complete": ["activities:read"],
  "hours:create": ["hours:read"],
  "hours:update": ["hours:read"],
  "hours:delete": ["hours:read"],
  "hours:approve": ["hours:read"],
  "reports:export": ["reports:read-summary"],
  "settings:update": ["settings:read"],
  "billing:manage": ["billing:read", "billing:write"],
}

export const expandPermissionDependencies = (
  permissions: readonly Permission[],
): Permission[] => {
  const expanded = new Set<Permission>()

  const addWithDependencies = (permission: Permission) => {
    if (expanded.has(permission)) return
    expanded.add(permission)

    for (const dependency of PERMISSION_DEPENDENCIES[permission] ?? []) {
      addWithDependencies(dependency)
    }
  }

  for (const permission of permissions) addWithDependencies(permission)
  return [...expanded]
}

/**
 * Norwegian labels for each permission.
 * Used in UI for displaying human-readable permission names.
 */
export const PERMISSION_LABELS: Record<Permission, string> = {
  "*": "Alle rettigheter",
  "users:read": "Se brukere",
  "users:write": "Administrere brukere",
  "persons:read": "Se medlemmer",
  "persons:write": "Administrere medlemmer",
  "tasks:read": "Se aktiviteter",
  "tasks:write": "Redigere alle aktiviteter",
  "tasks:create": "Opprette aktiviteter",
  "tasks:assign": "Melde seg på aktiviteter",
  "billing:read": "Se timelister",
  "billing:write": "Endre fakturadata",
  "billing:export": "Eksportere fakturadata",
  "organization-hours:read": "Se organisasjonens timeoversikt",
  "settings:read": "Se innstillinger",
  "settings:write": "Endre innstillinger",
  "teams:manage": "Administrere lag",
  "roles:manage": "Administrere roller",
  "activityTypes:manage": "Administrere aktivitetstyper",
  "campaigns:send": "Sende kampanjer",
  "campaigns:settings": "Administrere kampanjeavsender",
  "automation:manage": "Administrere automatiseringer",
  "users:create": "Opprette brukere",
  "users:update": "Redigere brukere",
  "users:deactivate": "Deaktivere brukere",
  "users:delete": "Slette brukere",
  "users:import": "Importere brukere",
  "users:assign-role": "Tildele roller til brukere",
  "users:assign-team": "Administrere brukeres grupper",
  "users:adjust-hours": "Justere brukeres timer",
  "users:update-identity": "Endre brukeres identitet",
  "users:activate": "Aktivere brukere",
  "users:assign-managed-teams": "Tildele gruppeansvar",
  "roles:read": "Se roller",
  "roles:create": "Opprette roller",
  "roles:update": "Redigere roller",
  "roles:delete": "Slette roller",
  "roles:assign": "Tildele roller",
  "persons:create": "Opprette medlemmer",
  "persons:update": "Redigere medlemmer",
  "persons:delete": "Slette medlemmer",
  "persons:link": "Administrere medlemskoblinger",
  "persons:assign-team": "Administrere medlemmers grupper",
  "persons:activate": "Aktivere medlemmer",
  "persons:deactivate": "Deaktivere medlemmer",
  "persons:adjust-hours": "Justere medlemmers timer",
  "persons:manage-billing": "Administrere fakturakontakt",
  "teams:read": "Se grupper",
  "teams:create": "Opprette grupper",
  "teams:update": "Redigere grupper",
  "teams:delete": "Slette grupper",
  "teams:assign-members": "Administrere gruppemedlemmer",
  "activities:read": "Se aktiviteter",
  "activities:create": "Opprette aktiviteter",
  "activities:update": "Redigere aktiviteter",
  "activities:delete": "Slette aktiviteter",
  "activities:register-self": "Melde seg på aktiviteter",
  "activities:assign-users": "Administrere aktivitetspåmeldinger",
  "activities:complete": "Fullføre aktiviteter",
  "hours:read": "Se timer",
  "hours:create": "Registrere timer",
  "hours:update": "Redigere timer",
  "hours:delete": "Slette timer",
  "hours:approve": "Godkjenne timer",
  "reports:read-summary": "Se rapportoversikter",
  "reports:export": "Eksportere rapporter",
  "settings:update": "Endre organisasjonsinnstillinger",
  "billing:manage": "Administrere fakturering og Stripe",
}

/**
 * Permission groups organized by UI page/feature.
 * Mirrors the sidebar navigation structure exactly for intuitive permission management.
 *
 * Structure matches Sidebar.tsx:
 * - Main Navigation: Dashboard, Aktiviteter, Mine relasjoner, Timelisterapport
 * - Brukere & Roller: Brukeradministrasjon, Personer og koblinger, Gruppebehandling, Rollebehandling
 * - Import & Synkronisering: Importer aktiviteter, Importer fra Spond, Kalendersynkronisering
 * - System: Aktivitetstyper, aktivitetsmaler, kampanjer, e-postvarsler, Årlige timekrav
 * - Fakturering: Fakturainnstillinger, Fakturaer
 */
export const PERMISSION_GROUPS: Record<
  string,
  { label: string; description?: string; permissions: Permission[] }
> = {
  // ============================================================================
  // HOVEDNAVIGASJON
  // ============================================================================
  dashboard: {
    label: "Dashboard",
    description: "Oversiktsside med statistikk og hurtighandlinger",
    permissions: ["users:write", "teams:manage"],
  },
  activities: {
    label: "Aktiviteter",
    description: "Se, opprette og melde seg på aktiviteter",
    permissions: ["tasks:read", "tasks:write", "tasks:create", "tasks:assign"],
  },
  relations: {
    label: "Mine relasjoner",
    description: "Se egne relasjoner",
    permissions: ["persons:read"],
  },
  report: {
    label: "Timelisterapport",
    description: "Se timelister og timestatistikk for alle brukere",
    permissions: ["billing:read", "organization-hours:read"],
  },

  // ============================================================================
  // ADMINISTRASJON - Brukere & Roller
  // ============================================================================
  usersAndRoles: {
    label: "Brukere og roller",
    description: "Brukeradministrasjon, medlemmer, grupper og roller",
    permissions: [
      "users:read",
      "users:write",
      "persons:write",
      "teams:manage",
      "roles:manage",
    ],
  },

  // ============================================================================
  // ADMINISTRASJON - System
  // ============================================================================
  system: {
    label: "System",
    description:
      "Aktivitetstyper, aktivitetsmaler, kampanjer, e-postvarsler og innstillinger",
    permissions: [
      "activityTypes:manage",
      "settings:read",
      "settings:write",
      "campaigns:send",
      "campaigns:settings",
      "automation:manage",
    ],
  },

  // ============================================================================
  // ADMINISTRASJON - Fakturering
  // ============================================================================
  billing: {
    label: "Fakturering",
    description:
      "Fakturainnstillinger, fakturaer og organisasjonens timeoversikt",
    permissions: ["billing:write", "billing:export", "organization-hours:read"],
  },
}

/**
 * Legacy permission groups organized by resource type.
 * Kept for backward compatibility with existing code.
 * @deprecated Use PERMISSION_GROUPS instead
 */
export const PERMISSION_GROUPS_BY_RESOURCE: Record<
  string,
  { label: string; permissions: Permission[] }
> = {
  users: {
    label: "Brukere",
    permissions: ["users:read", "users:write"],
  },
  persons: {
    label: "Barn/Medlemmer",
    permissions: ["persons:read", "persons:write"],
  },
  tasks: {
    label: "Aktiviteter",
    permissions: ["tasks:read", "tasks:write", "tasks:create", "tasks:assign"],
  },
  billing: {
    label: "Fakturering",
    permissions: [
      "billing:read",
      "billing:write",
      "billing:export",
      "organization-hours:read",
    ],
  },
  settings: {
    label: "Innstillinger",
    permissions: ["settings:read", "settings:write"],
  },
  management: {
    label: "Administrasjon",
    permissions: [
      "teams:manage",
      "roles:manage",
      "activityTypes:manage",
      "campaigns:send",
      "campaigns:settings",
      "automation:manage",
    ],
  },
}
